'use client';

import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useStore } from 'zustand';

import type { FeatureFlagAdapter } from '@/feature-flags/client/adapter.types';
import { createDefaultAdapter } from '@/feature-flags/client/create-adapter';
import { EVALUATED_FLAG_KEYS, FEATURES, getFeatureNamesByFlagKey } from '@/feature-flags/consts/features.const';
import type { FeatureFlagKey } from '@/feature-flags/consts/flags.const';
import { authStore } from '@/store/auth.store';
import { generateEvaluationId, getCurrentEvaluationId } from '@/feature-flags/utils/correlation';
import { logError, logInfo, logWarning } from '@/feature-flags/utils/logger';
import { withTimeout } from '@/feature-flags/utils/with-timeout';
import { toNanoseconds } from '@/logger/logger';

const EVALUATION_TIMEOUT_MS = 10000;
const MAX_INIT_ATTEMPTS = 3;
const INIT_RETRY_BASE_DELAY_MS = 300;

export interface FlagState {
  variant: string | null;
  attachment: string | null;
  match: boolean;
  values: Record<string, unknown>;
  loading: boolean;
  error: Error | null;
  isFallback: boolean;
}

export interface FeatureFlagContextValue {
  flags: Record<string, FlagState>;
  isReady: boolean;
  error: Error | null;
  adapterName: string | null;
  refetch: () => Promise<void>;
}

const PENDING_FLAG_STATE: FlagState = {
  variant: null,
  attachment: null,
  match: false,
  values: {},
  loading: true,
  error: null,
  isFallback: true,
};

function parseFlagValues(flagKey: FeatureFlagKey, attachment: string | null): Record<string, unknown> {
  return getFeatureNamesByFlagKey(flagKey).reduce<Record<string, unknown>>((accumulator, name) => {
    accumulator[name] = FEATURES[name].parse(attachment);
    return accumulator;
  }, {});
}

function createFlagStates(state: FlagState): Record<string, FlagState> {
  return EVALUATED_FLAG_KEYS.reduce<Record<string, FlagState>>((accumulator, flagKey) => {
    accumulator[flagKey] = { ...state };
    return accumulator;
  }, {});
}

const FeatureFlagContext = createContext<FeatureFlagContextValue | undefined>(undefined);

export interface FeatureFlagProviderProps {
  children: React.ReactNode;
  userId?: string;
  adapter?: FeatureFlagAdapter;
}

export function FeatureFlagProvider({ children, userId: userIdProp, adapter: adapterProp }: FeatureFlagProviderProps) {
  const userIdFromStore = useStore(authStore, (state) => state.authenticatedUser?.id);
  const userId = userIdProp ?? userIdFromStore;

  const [flags, setFlags] = useState<Record<string, FlagState>>(() => createFlagStates(PENDING_FLAG_STATE));
  const [isReady, setIsReady] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [adapterName, setAdapterName] = useState<string | null>(null);

  const adapterRef = useRef<FeatureFlagAdapter | null>(null);
  const initRef = useRef(false);
  const settledFlagsRef = useRef<Set<string>>(new Set());
  const evaluationRef = useRef(0);

  useEffect(() => {
    if (initRef.current) {
      return;
    }

    initRef.current = true;

    async function initialize() {
      let lastError: Error | null = null;

      for (let attempt = 0; attempt < MAX_INIT_ATTEMPTS; attempt += 1) {
        try {
          const adapter = adapterProp ?? createDefaultAdapter();
          await adapter.initialize();

          adapterRef.current = adapter;
          setAdapterName(adapter.name);
          setIsReady(true);
          logInfo('Feature flag adapter initialized', {
            feature_flag: { event: 'initialized', adapter: adapter.name, attempt },
          });
          return;
        } catch (attemptError) {
          lastError = attemptError instanceof Error ? attemptError : new Error(String(attemptError));
          logWarning('Feature flag adapter init attempt failed', {
            feature_flag: { event: 'init_attempt_failed', attempt, max_attempts: MAX_INIT_ATTEMPTS },
            error: lastError,
          });

          if (attempt < MAX_INIT_ATTEMPTS - 1) {
            await new Promise((resolve) => {
              setTimeout(resolve, INIT_RETRY_BASE_DELAY_MS * (attempt + 1));
            });
          }
        }
      }

      const initError = lastError ?? new Error('Failed to initialize feature flag adapter');
      setError(initError);
      setIsReady(true);
      logError('Failed to initialize feature flag adapter', initError, {
        feature_flag: { event: 'initialization_failed', max_attempts: MAX_INIT_ATTEMPTS },
      });
    }

    initialize();
  }, [adapterProp]);

  const evaluateAll = useCallback(async () => {
    const adapter = adapterRef.current;

    if (!adapter) {
      return;
    }

    evaluationRef.current += 1;
    const evaluation = evaluationRef.current;
    const isStale = () => evaluation !== evaluationRef.current;

    generateEvaluationId();

    setFlags((current) => EVALUATED_FLAG_KEYS.reduce<Record<string, FlagState>>((accumulator, flagKey) => {
      accumulator[flagKey] = {
        ...current[flagKey],
        loading: !settledFlagsRef.current.has(flagKey),
        error: null,
      };
      return accumulator;
    }, { ...current }));

    await Promise.all(EVALUATED_FLAG_KEYS.map(async (flagKey) => {
      const startedAt = performance.now();

      try {
        const result = await withTimeout(
          adapter.evaluate(flagKey, { userId }),
          EVALUATION_TIMEOUT_MS,
          `Evaluation of ${flagKey}`,
        );

        if (isStale()) {
          return;
        }

        const values = result.match ? parseFlagValues(flagKey, result.attachment) : {};

        logInfo('Flag evaluated', {
          feature_flag: {
            event: 'evaluated',
            flag_key: flagKey,
            variant: result.variant,
            match: result.match,
            value_keys: Object.keys(values),
            evaluation_id: getCurrentEvaluationId(),
          },
          ...(userId ? { user: { id: userId } } : {}),
          duration: toNanoseconds(performance.now() - startedAt),
        });

        settledFlagsRef.current.add(flagKey);

        setFlags((current) => ({
          ...current,
          [flagKey]: {
            variant: result.match ? result.variant : 'no-match-fallback',
            attachment: result.attachment,
            match: result.match,
            values,
            loading: false,
            error: null,
            isFallback: !result.match,
          },
        }));
      } catch (evaluationError) {
        const flagError = evaluationError instanceof Error ? evaluationError : new Error(String(evaluationError));

        if (isStale()) {
          return;
        }

        logError('Flag evaluation failed', flagError, {
          feature_flag: {
            event: 'evaluation_failed',
            flag_key: flagKey,
            evaluation_id: getCurrentEvaluationId(),
          },
          ...(userId ? { user: { id: userId } } : {}),
          duration: toNanoseconds(performance.now() - startedAt),
        });

        settledFlagsRef.current.add(flagKey);

        setFlags((current) => ({
          ...current,
          [flagKey]: { ...PENDING_FLAG_STATE, loading: false, error: flagError },
        }));
      }
    }));
  }, [userId]);

  useEffect(() => {
    if (!isReady) {
      return;
    }

    if (error) {
      setFlags(createFlagStates({ ...PENDING_FLAG_STATE, loading: false, error }));
      return;
    }

    evaluateAll();
  }, [isReady, error, evaluateAll]);

  const value = useMemo<FeatureFlagContextValue>(
    () => ({ flags, isReady, error, adapterName, refetch: evaluateAll }),
    [flags, isReady, error, adapterName, evaluateAll],
  );

  return (
    <FeatureFlagContext.Provider value={value}>
      {children}
    </FeatureFlagContext.Provider>
  );
}

export function useFeatureFlagContext(): FeatureFlagContextValue {
  const context = useContext(FeatureFlagContext);

  if (!context) {
    throw new Error('useFeatureFlagContext must be used within FeatureFlagProvider');
  }

  return context;
}

export function useFlagState(flagKey: FeatureFlagKey): FlagState {
  const { flags } = useFeatureFlagContext();

  return flags[flagKey] ?? PENDING_FLAG_STATE;
}
